import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  CalendarToday, 
  Description, 
  Person, 
  Add, 
  Upload,
  VideoCall,
  CheckCircle,
  Error as ErrorIcon,
  Download,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { appointmentAPI, formsAPI, Appointment, FMLAForm } from '../services/apiService';
import VideoConsultation from '../components/VideoConsultation';

const PatientDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<{
    show: boolean;
    type: 'success' | 'error';
    message: string;
  }>({ show: false, type: 'success', message: '' });
  const [videoConsultation, setVideoConsultation] = useState<{
    open: boolean;
    appointment: Appointment | null;
  }>({
    open: false,
    appointment: null,
  });
  
  const [dashboardData, setDashboardData] = useState<{
    upcomingAppointments: Appointment[];
    completedAppointments: Appointment[];
    recentDocuments: any[];
    fmlaForms: { appointmentId: string; form: FMLAForm }[];
    serviceInfo: {
      serviceType: 'fmla' | 'fmla-std';
      paymentAmount: number;
      employmentState: string;
    };
  } | null>(null);

  const hasNoAppointments = !dashboardData?.upcomingAppointments?.length;
  const hasPaid = user?.paymentAmount && user.paymentAmount > 0;

  useEffect(() => {
    checkPaymentStatus();
    loadDashboardData();
  }, [searchParams]);

  const checkPaymentStatus = () => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (payment === 'success') {
      setPaymentStatus({
        show: true,
        type: 'success',
        message: 'Payment successful! Your appointment is being confirmed. You will receive an email confirmation shortly.'
      });
      // Clean up URL parameters
      setSearchParams({});
    } else if (payment === 'cancelled') {
      setPaymentStatus({
        show: true,
        type: 'error',
        message: 'Payment was cancelled. You can try booking again at any time.'
      });
      // Clean up URL parameters
      setSearchParams({});
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load appointments only (documents are embedded in appointments)
      const appointmentsResponse = await appointmentAPI.list();
      
      const upcomingAppointments = appointmentsResponse.filter(
        (apt: Appointment) => new Date(apt.dateTime) > new Date()
      );

      const completedAppointments = appointmentsResponse.filter(
        (apt: Appointment) => apt.status === 'completed'
      );

      const fmlaForms: { appointmentId: string; form: FMLAForm }[] = [];
      appointmentsResponse.forEach((apt: Appointment) => {
        if (apt.generatedForms) {
          apt.generatedForms.forEach(form => {
            fmlaForms.push({ appointmentId: apt.appointmentId, form });
          });
        }
      });
      
      // Extract documents from all appointments for recent documents view
      const allDocuments: any[] = [];
      appointmentsResponse.forEach((apt: Appointment) => {
        if (apt.patientDocuments) {
          apt.patientDocuments.forEach(doc => {
            allDocuments.push({
              ...doc,
              appointmentId: apt.appointmentId,
              documentId: `${apt.appointmentId}-${doc.fileName}` // Create unique ID
            });
          });
        }
      });
      
      // Sort by upload date and get most recent 5
      const recentDocuments = allDocuments
        .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime())
        .slice(0, 5);
      
      setDashboardData({
        upcomingAppointments,
        completedAppointments,
        recentDocuments,
        fmlaForms,
        serviceInfo: {
          serviceType: (user?.serviceType as 'fmla' | 'fmla-std') || 'fmla',
          paymentAmount: user?.paymentAmount || 100,
          employmentState: user?.employmentState || 'Not specified'
        }
      });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const formatServiceType = (serviceType: 'fmla' | 'fmla-std') => {
    return serviceType === 'fmla' ? 'FMLA Certification' : 'FMLA + STD Certification';
  };

  const formatAppointmentDate = (dateTime: string) => {
    return new Date(dateTime).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const openVideoConsultation = (appointment: Appointment) => {
    setVideoConsultation({
      open: true,
      appointment,
    });
  };

  const closeVideoConsultation = () => {
    setVideoConsultation({
      open: false,
      appointment: null,
    });
  };

  const handlePaymentStatusClose = () => {
    setPaymentStatus({ ...paymentStatus, show: false });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (hasNoAppointments) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {paymentStatus.show && paymentStatus.type === 'success' && (
          <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircle />}>
            {paymentStatus.message}
          </Alert>
        )}

        <Card sx={{ p: 4, textAlign: 'center', background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', color: 'white' }}>
          <Typography variant="h4" gutterBottom>
            Welcome to FMLA Doctor, {user?.name}!
          </Typography>
          {hasPaid ? (
            <>
              <Typography variant="h6" gutterBottom sx={{ opacity: 0.9 }}>
                Your payment has been received. Book your consultation now!
              </Typography>
              <Button 
                variant="contained"
                size="large" 
                onClick={() => navigate('/dashboard/booking')}
                sx={{ 
                  mt: 3, 
                  backgroundColor: 'white', 
                  color: 'primary.main',
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
                startIcon={<CalendarToday />}
              >
                Schedule Your Appointment
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h6" gutterBottom sx={{ opacity: 0.9 }}>
                Book your FMLA consultation with a licensed healthcare provider
              </Typography>
              <Button 
                variant="contained"
                size="large" 
                onClick={() => navigate('/services')}
                sx={{ 
                  mt: 3, 
                  backgroundColor: 'white', 
                  color: 'primary.main',
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
                startIcon={<Add />}
              >
                Get Started
              </Button>
            </>
          )}
        </Card>
        
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Employment State: {user?.employmentState || 'Not specified'}
          </Typography>
        </Box>

        <Snackbar
          open={paymentStatus.show}
          autoHideDuration={6000}
          onClose={handlePaymentStatusClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={handlePaymentStatusClose} 
            severity={paymentStatus.type}
            variant="filled"
          >
            {paymentStatus.message}
          </Alert>
        </Snackbar>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Welcome Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Welcome back, {user?.name || 'Patient'}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Manage your FMLA consultation and documentation
        </Typography>
      </Box>

      {/* Service Information Card */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', color: 'white' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" gutterBottom>
                {dashboardData?.serviceInfo ? formatServiceType(dashboardData.serviceInfo.serviceType) : 'FMLA Service'}
              </Typography>
              <Typography variant="body1">
                Employment State: {dashboardData?.serviceInfo?.employmentState || 'Not specified'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Service Cost: ${dashboardData?.serviceInfo?.paymentAmount || 100}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} textAlign="right">
              <Chip 
                label="Active" 
                color="secondary" 
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }} 
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Main Actions */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarToday color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Schedule Appointment</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                Book a consultation with a licensed healthcare provider
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<Add />}
                onClick={() => navigate('/appointments/schedule')}
                sx={{ mt: 2 }}
              >
                Schedule Now
              </Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Upload color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Upload Documents</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                Upload medical records or supporting documentation
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<Upload />}
                onClick={() => navigate('/documents/upload')}
                sx={{ mt: 2 }}
              >
                Upload Files
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* FMLA Forms Section */}
      {(dashboardData?.fmlaForms?.length ?? 0) > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Your FMLA Certification Forms</Typography>
              <Button size="small" onClick={() => navigate('/documents')}>View All</Button>
            </Box>
            {dashboardData!.fmlaForms.map(({ appointmentId, form }, idx) => (
              <Box key={idx} sx={{ mb: 1, p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="subtitle2">{form.formType.toUpperCase()}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Generated: {new Date(form.generatedAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Download />}
                  onClick={async () => {
                    try {
                      const formId = form.s3Key.split('/').pop() || form.s3Key;
                      const { downloadUrl } = await formsAPI.getDownloadUrl(appointmentId, formId);
                      window.open(downloadUrl, '_blank');
                    } catch {
                    }
                  }}
                >
                  Download
                </Button>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Quick Access Grid */}
      <Grid container spacing={3}>
        {/* Upcoming Appointments */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Upcoming Appointments</Typography>
                <Button size="small" onClick={() => navigate('/appointments')}>
                  View All
                </Button>
              </Box>
              {dashboardData?.upcomingAppointments.length ? (
                dashboardData.upcomingAppointments.slice(0, 3).map((appointment) => (
                  <Box key={appointment.appointmentId} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2">
                      {formatAppointmentDate(appointment.dateTime)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {appointment.providerEmail}
                    </Typography>
                    <Chip 
                      label={appointment.status} 
                      size="small" 
                      color={appointment.status === 'booked' ? 'success' : 'default'}
                      sx={{ mt: 1 }}
                    />
                    {appointment.consultationStatus === 'ready' && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<VideoCall />}
                        onClick={() => openVideoConsultation(appointment)}
                        sx={{ mt: 1, ml: 1 }}
                      >
                        Join Video Consultation
                      </Button>
                    )}
                  </Box>
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    No upcoming appointments.
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<CalendarToday />}
                    onClick={() => navigate('/dashboard/booking')}
                    sx={{ mt: 1 }}
                  >
                    Schedule Now
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Documents */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Recent Documents</Typography>
                <Button size="small" onClick={() => navigate('/documents')}>
                  View All
                </Button>
              </Box>
              {dashboardData?.recentDocuments.length ? (
                dashboardData.recentDocuments.slice(0, 3).map((document) => (
                  <Box key={document.documentId} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2">
                      {document.fileName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Uploaded: {new Date(document.uploadedAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(document.fileSize / 1024 / 1024).toFixed(1)} MB
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No documents uploaded yet. Upload your medical records to get started!
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Links */}
      <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button 
          variant="outlined" 
          startIcon={<Description />}
          onClick={() => navigate('/documents')}
        >
          My Documents & Forms
        </Button>
        <Button 
          variant="outlined" 
          startIcon={<Description />}
          onClick={() => navigate('/profile')}
        >
          Update Profile
        </Button>
      </Box>

      {/* Payment Status Notification */}
      <Snackbar
        open={paymentStatus.show}
        autoHideDuration={6000}
        onClose={handlePaymentStatusClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handlePaymentStatusClose} 
          severity={paymentStatus.type}
          variant="filled"
          sx={{ width: '100%', minWidth: 400 }}
          icon={paymentStatus.type === 'success' ? <CheckCircle /> : <ErrorIcon />}
        >
          {paymentStatus.message}
        </Alert>
      </Snackbar>

      {/* Payment Success Dialog */}
      {paymentStatus.show && paymentStatus.type === 'success' && (
        <Dialog
          open={paymentStatus.show}
          onClose={handlePaymentStatusClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h5" component="div">
              Payment Successful!
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            <Typography variant="body1" paragraph>
              Thank you for your payment. Your FMLA consultation appointment is being processed.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              You will receive:
            </Typography>
            <Box component="ul" sx={{ textAlign: 'left', display: 'inline-block' }}>
              <Typography component="li" variant="body2">Email confirmation with appointment details</Typography>
              <Typography component="li" variant="body2">Provider information and meeting link</Typography>
              <Typography component="li" variant="body2">Pre-consultation assessment forms</Typography>
            </Box>
            <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>Next Steps:</strong> Check your email for confirmation and complete any pre-consultation forms sent to you.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button 
              onClick={handlePaymentStatusClose}
              variant="contained"
              size="large"
            >
              Got it!
            </Button>
            <Button 
              onClick={() => navigate('/appointments')}
              variant="outlined"
              size="large"
            >
              View Appointments
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Video Consultation Dialog */}
      {videoConsultation.appointment && (
        <VideoConsultation
          appointment={videoConsultation.appointment}
          open={videoConsultation.open}
          onClose={closeVideoConsultation}
          userType={(user?.userType as 'patient' | 'provider') || 'patient'}
        />
      )}
    </Container>
  );
};

export default PatientDashboard;